import { BaseItem } from './common';
import * as VisitPatientStatus from './visitPatientStatus';

export enum IntentStatus {
  Canceled = 'CANCELED',
  Processing = 'PROCESSING',
  RequiresAction = 'REQUIRES_ACTION',
  RequiresCapture = 'REQUIRES_CAPTURE',
  RequiresConfirmation = 'REQUIRES_CONFIRMATION',
  RequiresPaymentMethod = 'REQUIRES_PAYMENT_METHOD',
  Succeeded = 'SUCCEEDED',
}

export enum DisplayStatus {
  LostDispute = 'LOST_DISPUTE',
  Refunded = 'REFUNDED',
  RefundedAndLostDispute = 'REFUNDED_AND_LOST_DISPUTE',
}

export interface PaymentMethodPayload {
  last4: string | null;
  brand: string | null;
  exp_month: number | null;
  exp_year: number | null;
}

export enum FeeType {
  CoPay = 'COPAY',
  VisitFee = 'VISIT_FEE',
}

export enum Currency {
  AUD = 'AUD',
  CAD = 'CAD',
  EUR = 'EUR',
  GBP = 'GBP',
  USD = 'USD',
}

export enum PaymentType {
  Balance = 'BALANCE',
  Custom = 'CUSTOM',
  Fee = 'FEE',
  FeeAndBalance = 'FEE_AND_BALANCE',
  FeeAndPartialBalance = 'FEE_AND_PARTIAL_BALANCE',
  FeeAndPrepay = 'FEE_AND_PREPAY',
  FeeAndPrepayAndPartialBalance = 'FEE_AND_PREPAY_AND_PARTIAL_BALANCE',
  PartialBalance = 'PARTIAL_BALANCE',
  Prepay = 'PREPAY',
  PrepayAndBalance = 'PREPAY_AND_BALANCE',
  PrepayAndPartialBalance = 'PREPAY_AND_PARTIAL_BALANCE',
  Total = 'TOTAL',
}

export enum CardBrand {
  Amex = 'AMEX',
  Diners = 'DINERS',
  Discover = 'DISCOVER',
  JCB = 'JCB',
  MasterCard = 'MASTERCARD',
  UnionPay = 'UNIONPAY',
  Unknown = 'UNKNOWN',
  Visa = 'VISA',
}

interface TransactionData {
  visitFeeAmount: number;
  feeType: FeeType;
  currency: Currency;
  amount: number;
  previousBalance: number;
  selectedPaymentType: PaymentType;
  applicationFeeAmount: number | null;
  prepayAmount: number | null;
  cardBrand: CardBrand;
  partialBalanceAmount?: number | null;
}

export interface EncounterPayment extends TransactionData {
  encounterId: string;
  intentId: string;
  intentStatus: IntentStatus;
  referenceCode: string | null;
}

export interface PaymentInfo extends TransactionData {
  clientSecret: string;
  intentStatus: IntentStatus;
  paymentMethods: PaymentMethodPayload[];
  updated: string;
  patientSkipFeedback: string;
}

export interface PaymentDashboardResponse {
  results: PaymentDashboardEncounter[];
  page: number;
  hasNextPage: boolean;
  filterOptions: {
    practices: {
      id: string;
      value: string;
    }[];
    providers: {
      id: string;
      value: string;
    }[];
    mrn: string;
    referenceNumber: string;
  };
}

export enum PaymentDashboardSortColumn {
  appointmentDate = 'appointmentDate',
  providerName = 'providerName',
  patientName = 'patientName',
  practiceName = 'practiceName',
}

export type PaymentDashboardOrder = 'asc' | 'desc';

export interface PaymentDashboardEncounter {
  id: string;
  payoutId: string | null;
  patientName: string;
  mrn: string;
  appointmentDate: string;
  providerName: string;
  practiceName: string;
  collectionAmount: number;
  postFeeCollectionAmount: number;
  balanceAmount: number;
  visitFeeAmount: number;
  applicationFeeAmount: number;
  collectionStatus: IntentStatus | DisplayStatus;
  paymentReferenceNumber: string;
  payoutPaidTimestamp: string | null;
  ehrEncounterId: string | null;
  refundable?: boolean;
  prepayAmount: number;
  partialBalanceAmount: number;
  selectedPaymentType: PaymentType;
  refundedAmount: number | null;
}

export interface PaymentDashboardQuery {
  after?: Date;
  before?: Date;
  page?: number;
  pageSize?: number;
  sortBy?: PaymentDashboardSortColumn;
  order?: PaymentDashboardOrder;
  practiceIds?: string[];
  providerUserIds?: string[];
  mrn?: string;
  referenceNumber?: string;
}

export interface PaymentFormFieldValue {
  name?: string;
  selectedIndex?: number;
  skipped?: boolean;
  success?: boolean;
  confirmedWithStatus?: IntentStatus | null;
}

export enum PayoutStatus {
  Pending = 'PENDING',
  Paid = 'PAID',
  InTransit = 'IN_TRANSIT',
  Canceled = 'CANCELED',
  Failed = 'FAILED',
}

export enum PayoutSource {
  Automated = 'AUTOMATED',
  Cron = 'CRON',
  Manual = 'MANUAL',
}

export interface Payout extends BaseItem {
  stripeId: string;
  accountId: string;
  status: PayoutStatus;
  amount: number;
  currency: Currency;
  payoutStart: Date | null;
  payoutEnd: Date | null;
  source: PayoutSource;
  issuedTimestamp: Date | null;
  paidTimestamp: Date | null;
  terminatedTimestamp: Date | null;
  livemode: boolean;
}

export enum PaymentEventStatus {
  Completed = 'Completed',
  Failed = 'Failed',
  Started = 'Started',
  Pending = 'Pending',
  Canceled = 'Canceled',
}

export enum PaymentEventType {
  Payment = 'Payment',
  Refund = 'Refund',
  Job = 'Job',
  VisitStatus = 'VisitStatus',
}

interface PaymentEventMetadata {
  description: string | null;
  [key: string]: string | null;
}

export interface PaymentEvent {
  created: string;
  name: string;
  status: PaymentEventStatus | VisitPatientStatus.Status;
  type: PaymentEventType;
  metadata: PaymentEventMetadata;
}
